import { useState, useRef } from 'react'
import api, { Target, ImportPreviewResponse } from '../../services/api'
import Button from '../common/Button'
import LoadingSpinner from '../common/LoadingSpinner'

interface CsvImportModalProps {
  organisationId: string
  onClose: () => void
  onSuccess: () => void
}

const CsvImportModal: React.FC<CsvImportModalProps> = ({ organisationId, onClose, onSuccess }) => {
  const [file, setFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<ImportPreviewResponse | null>(null)
  const [parsing, setParsing] = useState(false)
  const [importing, setImporting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0]
    if (!selectedFile) return

    setFile(selectedFile)
    setPreview(null)
    setError(null)

    try {
      setParsing(true)
      const targets = await api.targetPool.parseCsv(selectedFile)
      const previewResult = await api.targetPool.previewImport(organisationId, targets)
      setPreview(previewResult)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to parse CSV')
    } finally {
      setParsing(false)
    }
  }

  const handleImport = async () => {
    if (!file || !preview) return

    try {
      setImporting(true)
      setError(null)
      const targets = await api.targetPool.parseCsv(file)
      const result = await api.targetPool.bulkImport(organisationId, targets)
      onSuccess()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to import targets')
      setImporting(false)
    }
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    
    const droppedFile = e.dataTransfer.files?.[0]
    if (droppedFile && droppedFile.name.endsWith('.csv')) {
      if (fileInputRef.current) {
        const dataTransfer = new DataTransfer()
        dataTransfer.items.add(droppedFile)
        fileInputRef.current.files = dataTransfer.files
        handleFileSelect({ target: fileInputRef.current } as any)
      }
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            Import Targets from CSV
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            CSV should have columns: Email, Given Name, Surname, Job Title, Department
          </p>
        </div>

        <div className="px-6 py-4 flex-1 overflow-y-auto space-y-4">
          {!file ? (
            <div
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 text-center hover:border-blue-500 dark:hover:border-blue-400 transition-colors cursor-pointer"
              onClick={() => fileInputRef.current?.click()}
            >
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                Drag and drop your CSV file here, or click to browse
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
                className="hidden"
              />
            </div>
          ) : parsing ? (
            <div className="flex flex-col items-center py-8">
              <LoadingSpinner />
              <p className="mt-4 text-sm text-gray-600 dark:text-gray-400">
                Parsing CSV file...
              </p>
            </div>
          ) : preview ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  {file.name}
                </p>
                <button
                  onClick={() => {
                    setFile(null)
                    setPreview(null)
                    if (fileInputRef.current) fileInputRef.current.value = ''
                  }}
                  className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400"
                >
                  Choose different file
                </button>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
                  <p className="text-sm text-gray-600 dark:text-gray-400">Valid</p>
                  <p className="text-2xl font-semibold text-green-600 dark:text-green-400">
                    {preview.valid_count}
                  </p>
                </div>
                <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-4">
                  <p className="text-sm text-gray-600 dark:text-gray-400">Duplicates</p>
                  <p className="text-2xl font-semibold text-yellow-600 dark:text-yellow-400">
                    {preview.duplicate_count}
                  </p>
                </div>
                <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-4">
                  <p className="text-sm text-gray-600 dark:text-gray-400">Invalid</p>
                  <p className="text-2xl font-semibold text-red-600 dark:text-red-400">
                    {preview.invalid_count}
                  </p>
                </div>
                <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-4">
                  <p className="text-sm text-gray-600 dark:text-gray-400">Wrong Domain</p>
                  <p className="text-2xl font-semibold text-orange-600 dark:text-orange-400">
                    {preview.domain_mismatch_count}
                  </p>
                </div>
              </div>

              {preview.errors && preview.errors.length > 0 && (
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                  <div className="bg-gray-50 dark:bg-gray-700 px-4 py-2 border-b border-gray-200 dark:border-gray-700">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      Issues Found ({preview.errors.length})
                    </p>
                  </div>
                  <div className="max-h-48 overflow-y-auto">
                    {preview.errors.slice(0, 10).map((errorMsg, idx) => (
                      <div
                        key={idx}
                        className="px-4 py-2 border-b border-gray-100 dark:border-gray-700 last:border-0"
                      >
                        <p className="text-xs text-gray-600 dark:text-gray-400">
                          {errorMsg}
                        </p>
                      </div>
                    ))}
                    {preview.errors.length > 10 && (
                      <div className="px-4 py-2 text-xs text-gray-500 dark:text-gray-400 italic">
                        ... and {preview.errors.length - 10} more errors
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : null}

          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex gap-3">
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={importing}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleImport}
            disabled={!preview || preview.valid_count === 0 || importing}
            className="flex-1"
          >
            {importing ? 'Importing...' : `Import ${preview?.valid_count || 0} Targets`}
          </Button>
        </div>
      </div>
    </div>
  )
}

export default CsvImportModal
